import { useEffect, useRef, useState } from "react";

import { useLiveMeetingStore } from "../store.ts";

/** No speech for this long while a meeting is live → prompt the user. */
const IDLE_MS = 5 * 60_000;
/** No response to the prompt for this long → end the meeting automatically. */
const GRACE_MS = 60_000;

export interface InactivityAutoEnd {
  /** Whether the "are you still there?" prompt is showing. */
  prompting: boolean;
  /** Seconds left before auto-end while prompting. */
  secondsLeft: number;
  /** User chose to stay — reset the idle timer and dismiss. */
  stay: () => void;
  /** User chose to end now (or grace elapsed). */
  endNow: () => void;
}

/**
 * Watches the live transcript for speech. While the meeting is actively capturing
 * audio (`active`) but no new speech has been transcribed for IDLE_MS, it shows a
 * prompt. If the user doesn't respond within GRACE_MS, `onAutoEnd` is called so a
 * forgotten, still-open meeting closes itself. Any new speech (or "Stay") resets
 * the timer and dismisses the prompt.
 */
export function useInactivityAutoEnd(active: boolean, onAutoEnd: () => void): InactivityAutoEnd {
  const transcriptLen = useLiveMeetingStore((s) => s.transcript.length);
  const livePartial = useLiveMeetingStore((s) => s.livePartial);

  const [prompting, setPrompting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(GRACE_MS / 1000));

  const lastActivityRef = useRef(Date.now());
  const promptStartRef = useRef(0);
  const onAutoEndRef = useRef(onAutoEnd);
  onAutoEndRef.current = onAutoEnd;

  // Any new speech (a final line or a streaming partial) counts as activity and
  // cancels an in-progress prompt.
  useEffect(() => {
    lastActivityRef.current = Date.now();
    setPrompting((p) => (p ? false : p));
  }, [transcriptLen, livePartial]);

  useEffect(() => {
    if (!active) {
      setPrompting(false);
      lastActivityRef.current = Date.now();
      return;
    }
    const id = setInterval(() => {
      const now = Date.now();
      if (!prompting) {
        if (now - lastActivityRef.current >= IDLE_MS) {
          promptStartRef.current = now;
          setSecondsLeft(Math.ceil(GRACE_MS / 1000));
          setPrompting(true);
        }
      } else {
        const remaining = GRACE_MS - (now - promptStartRef.current);
        if (remaining <= 0) {
          setPrompting(false);
          onAutoEndRef.current();
        } else {
          setSecondsLeft(Math.ceil(remaining / 1000));
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, [active, prompting]);

  function stay() {
    lastActivityRef.current = Date.now();
    setPrompting(false);
  }
  function endNow() {
    setPrompting(false);
    onAutoEndRef.current();
  }

  return { prompting, secondsLeft, stay, endNow };
}
